import React, { useState } from "react";
import Card from "../Card/Card";
import { useDispatch, useSelector } from "react-redux";
import { setIsFullScreenLoading } from "../../../redux/reducers/loadingReducer";
import {
  getAllUserDataForTemplate,
  updateUserSettings,
  uploadTemplate,
} from "../../../services/api/user.api";
import Modal from "../Modal/Modal";
import { FileUploader } from "react-drag-drop-files";
import Button from "../../atoms/Button/Button";
import { Download, Upload } from "@mui/icons-material";
import { RootState } from "../../../redux/store/store";
import { useTranslation } from "react-i18next";
import LanguageSelect from "../../atoms/LanguageSelect/LanguageSelect";
import FontSizeSelect, {
  FontSizeType,
} from "../../atoms/FontSizeSelect/FontSizeSelect";
import ThemeSelect, { ThemeType } from "../../atoms/ThemeSelect/ThemeSelect";
import {
  setUserFontSize,
  setUserLang,
  setUserTheme,
} from "../../../redux/reducers/settingsReducer";

interface SettingsCardProps {}

const SettingsCard = (props: SettingsCardProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const user = useSelector((state: RootState) => state.settings.user);

  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  const handleDownloadTemplate = () => {
    dispatch(setIsFullScreenLoading(true));
    getAllUserDataForTemplate(dispatch).then((response) => {
      dispatch(setIsFullScreenLoading(false));
      const data = response.result;
      const json = JSON.stringify(data);
      const blob = new Blob([json], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "doktorui-template.json";
      link.click();
    });
  };

  const fileTypes = ["JSON"];

  const handleFileUpload = (file: File | File[]) => {
    if (Array.isArray(file)) {
      return;
    }
    setUploadedFile(file);
  };

  const handleUploadTemplate = () => {
    if (uploadedFile) {
      dispatch(setIsFullScreenLoading(true));
      uploadTemplate(dispatch, uploadedFile).then((response) => {
        dispatch(setIsFullScreenLoading(false));
        window.location.reload();
      });
    }
  };

  const handleLangChange = (value: string) => {
    dispatch(setUserLang(value));
    localStorage.setItem("lang", value);
    updateUserSettings(dispatch, undefined, undefined, value);
  };

  const handleFontSizeChange = (value: FontSizeType) => {
    dispatch(setUserFontSize(value));
    localStorage.setItem("fontSize", value);
    updateUserSettings(dispatch, undefined, value);
  };

  const handleThemeChange = (value: ThemeType) => {
    dispatch(setUserTheme(value));
    localStorage.setItem("theme", value);
    updateUserSettings(dispatch, value);
  };

  return (
    <Card
      title={t("appSettings")}
      className="dui-settings-card"
      additionalActions={
        user?.subscription === "premium" ||
        user?.subscription === "pro" ||
        user?.subscription === "free" ? (
          <div className="dui-settings-additional-actions">
            <Button onClick={handleDownloadTemplate} icon={<Download />}>
              {t("downloadTemplate")}
            </Button>
            <Button onClick={() => setIsUploadOpen(true)} icon={<Upload />}>
              {t("uploadTemplate")}
            </Button>
          </div>
        ) : undefined
      }
    >
      <div className="dui-settings-card-field">
        <div className="dui-settings-card-field-title">{t("language")}:</div>
        <LanguageSelect onLangChange={handleLangChange} />
      </div>
      <div className="dui-settings-card-field">
        <div className="dui-settings-card-field-title">{t("fontSize")}:</div>
        <FontSizeSelect
          fontSize={user?.fontSize}
          onFontSizeChange={handleFontSizeChange}
        />
      </div>
      <div className="dui-settings-card-field">
        <div className="dui-settings-card-field-title">{t("theme")}:</div>
        <ThemeSelect theme={user?.theme} onThemeChange={handleThemeChange} />
      </div>
      <Modal
        open={isUploadOpen}
        onClose={() => setIsUploadOpen(false)}
        title={t("uploadTemplate")}
        onConfirm={handleUploadTemplate}
        isConfirmDisabled={!uploadedFile}
      >
        <div className="dui-setting-upload-file">
          <FileUploader
            handleChange={handleFileUpload}
            name="file"
            types={fileTypes}
            label={t("uploadTemplateLabel")}
            uploadedLabel={t("uploadTemplateSuccess")}
            classes="dui-setting-upload-file-input"
          />
        </div>
      </Modal>
    </Card>
  );
};

export default SettingsCard;
